// Reunderwriting Module (Tier 3) PR 3 — frontend types mirroring the
// PR 1 + PR 2 DTOs in src/PhoenixCapital.Core/DTOs/Reunderwriting/.
// Hand-maintained (the repo doesn't ship a TS generator); keep aligned
// with ReunderwritingDtos.cs.

import type { CriteriaLane, CriteriaItemStatus, CriteriaTier, LaneOutcome, DealQualityRating, DealAssignmentStatus, FailureStatus } from "@/api/dealFlow/types";

// Re-export common enums Deal Flow already typed.
export type { CriteriaLane, CriteriaTier, LaneOutcome, DealQualityRating, FailureStatus };

// ---- Reunderwriting-specific enums ----

export type CounterpartyKind = "Person" | "Entity" | "Business";

export type ReunderwritingStage =
  | "Underwriting"
  | "Closing"
  | "Closed"
  | "Declined"
  | "Withdrawn";

export type ReunderwritingSubState =
  | "AssignedToA1"
  | "ClosingInProgress"
  | "DeclinedFinal"
  | "WithdrawnFinal"
  | "ArchivedFinal";

export type DownstreamLoanEffect =
  | "StatusUpdateOnly"
  | "ObligorSwap"
  | "RetireAndCreateNew"
  | "CreateRelatedNew";

export type ReunderwritingAuthority =
  | "StandardTeam"
  | "FullTeam"
  | "SeniorUnderwriter"
  | "SpecialServicing";

// Stage progression labels — narrower than Deal Flow per spec (no Intake /
// PreApproval). Used by the pipeline board and case-detail tab gating.
export const REUNDERWRITING_ACTIVE_STAGES: ReunderwritingStage[] = [
  "Underwriting",
  "Closing",
  "Closed",
];

export const REUNDERWRITING_STAGE_LABEL: Record<ReunderwritingStage, string> = {
  Underwriting: "Underwriting",
  Closing: "Closing",
  Closed: "Closed",
  Declined: "Declined",
  Withdrawn: "Withdrawn",
};

export const REUNDERWRITING_SUB_STATE_LABEL: Record<ReunderwritingSubState, string> = {
  AssignedToA1: "Assigned to A1",
  ClosingInProgress: "Closing in progress",
  DeclinedFinal: "Declined",
  WithdrawnFinal: "Withdrawn",
  ArchivedFinal: "Archived",
};

export const DOWNSTREAM_LOAN_EFFECT_LABEL: Record<DownstreamLoanEffect, string> = {
  StatusUpdateOnly: "Status update only",
  ObligorSwap: "Obligor swap",
  RetireAndCreateNew: "Retire & create new loan",
  CreateRelatedNew: "Create related new loan",
};

// ---- DTOs ----

export interface ReunderwritingCaseListItemDto {
  id: string;
  caseNumber: string;
  eventTypeCode: string;
  stage: ReunderwritingStage;
  subState: ReunderwritingSubState;
  subjectLoanId: string;
  subjectLoanNumber: string | null;
  subjectCounterpartyKind: CounterpartyKind;
  subjectPersonId: string | null;
  subjectEntityId: string | null;
  subjectBusinessId: string | null;
  assignedA1Id: string | null;
  stageEnteredDate: string;
  isArchived: boolean;
  completedAt: string | null;
  createdAt: string;
}

export interface ReunderwritingCaseDto {
  id: string;
  caseNumber: string;
  eventTypeCode: string;
  stage: ReunderwritingStage;
  subState: ReunderwritingSubState;
  subjectLoanId: string;
  subjectLoanNumber: string | null;
  subjectCounterpartyKind: CounterpartyKind;
  subjectPersonId: string | null;
  subjectEntityId: string | null;
  subjectBusinessId: string | null;
  launchReason: string | null;
  assignedA1Id: string | null;
  stageEnteredDate: string;
  createdLoanId: string | null;
  createdLoanNumber: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

export interface ReunderwritingCaseListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  stage?: ReunderwritingStage[];
  subState?: ReunderwritingSubState[];
  eventTypeCode?: string;
  assignedA1Id?: string;
  subjectLoanId?: string;
  includeArchived?: boolean;
  includeClosed?: boolean;
}

export interface ReunderwritingChecklistItemDto {
  id: string;
  reunderwritingCaseId: string;
  catalogItemId: string;
  catalogCode: string;
  catalogTitle: string;
  lane: CriteriaLane;
  tier: CriteriaTier;
  status: CriteriaItemStatus;
  justification: string | null;
  certifiedByUserId: string | null;
  certifiedAt: string | null;
  failureReason: string | null;
  attachedDocumentId: string | null;
  escalatedToUserId: string | null;
  escalatedAt: string | null;
  waivedByUserId: string | null;
  waiverRationale: string | null;
  assessment: string | null;
  reportUploadedAttested: boolean;
  reportUploadedAttestedByUserId: string | null;
  reportUploadedAttestedAt: string | null;
}

export interface UpdateReunderwritingJustificationRequest {
  justification?: string;
  assessment?: string;
  reportUploadedAttested?: boolean;
}

export interface ReunderwritingLaneAssessmentDto {
  id: string;
  reunderwritingCaseId: string;
  lane: CriteriaLane;
  outcomePassFail: LaneOutcome;
  dealQualityRating: DealQualityRating;
  failureReason: string | null;
  assessedByUserId: string;
  assessedAt: string;
}

export interface IssueReunderwritingLaneAssessmentRequest {
  outcomePassFail: LaneOutcome;
  dealQualityRating: DealQualityRating;
  failureReason?: string | null;
}

export interface ReunderwritingOutcomeDto {
  id: string | null;
  reunderwritingCaseId: string;
  combinedDqr: DealQualityRating;
  overallOutcome: LaneOutcome;
  finalizedAt: string | null;
  finalizedByUserId: string | null;
  declinedReasonSummary: string | null;
  isFinalized: boolean;
}

export interface ReunderwritingFailureDto {
  id: string;
  reunderwritingCaseId: string;
  checklistItemId: string | null;
  lane: CriteriaLane;
  analystUserId: string;
  failureReason: string;
  status: FailureStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  certifiedResolved: boolean;
  createdAt: string;
}

export interface ReunderwritingAssignmentDto {
  id: string;
  reunderwritingCaseId: string;
  roleCode: string;
  status: DealAssignmentStatus;
  assignedUserId: string | null;
  assignedAt: string | null;
  assignedByUserId: string | null;
}

export interface UpsertReunderwritingAssignmentRequest {
  status: DealAssignmentStatus;
  assignedUserId?: string | null;
}

export interface ReunderwritingApprovalRequest {
  closingDate?: string;
  newBorrowerEntityId?: string;
  newBorrowerPersonId?: string;
  newBuyerEntityName?: string;
  salePrice?: number;
  principalShortfall?: number;
  assumedAmount?: number;
  expectedLoanVersionId?: number;
}

export interface ReunderwritingApprovalResult {
  caseId: string;
  caseNumber: string;
  effect: DownstreamLoanEffect;
  subjectLoanId: string;
  createdLoanId: string | null;
  createdLoanNumber: string | null;
  obligationIds: string[];
  alreadyApplied: boolean;
  completedAt: string;
}

export interface ReunderwritingEventTypeLookupDto {
  eventTypeCode: string;
  subjectParty: string;
  authority: ReunderwritingAuthority;
  downstreamLoanEffect: DownstreamLoanEffect;
  downstreamTriggerEvent: string;
  requiresTrailing6: boolean;
  dscrThreshold: number;
}

export interface DeclineReunderwritingCaseRequest {
  reasonCode: string;
  reasonDetail?: string;
}

export interface WithdrawReunderwritingCaseRequest {
  reasonCode: string;
  reasonDetail?: string;
}

export interface ArchiveReunderwritingCaseRequest {
  note?: string;
}

// Permission constants — mirror src/PhoenixCapital.Core/Constants/Permissions.cs
// (the Reunderwriting namespace lines 304–326).
export const REUNDERWRITING_PERMISSIONS = {
  Initiate: "Reunderwriting.Initiate",
  ViewCase: "Reunderwriting.ViewCase",
  AssignTeam: "Reunderwriting.AssignTeam",
  SelfClaimSlot: "Reunderwriting.SelfClaimSlot",
  MarkSlotNotApplicable: "Reunderwriting.MarkSlotNotApplicable",
  WriteUnderwritingTab: "Reunderwriting.WriteUnderwritingTab",
  WriteAnalystTab: "Reunderwriting.WriteAnalystTab",
  WriteClosingTab: "Reunderwriting.WriteClosingTab",
  CertifyChecklistItem: "Reunderwriting.CertifyChecklistItem",
  EscalateChecklistItem: "Reunderwriting.EscalateChecklistItem",
  DeclineChecklistItem: "Reunderwriting.DeclineChecklistItem",
  WaiveChecklistItem: "Reunderwriting.WaiveChecklistItem",
  IssueLanePassFail: "Reunderwriting.IssueLanePassFail",
  VerifyAnalystWork: "Reunderwriting.VerifyAnalystWork",
  ViewFailures: "Reunderwriting.ViewFailures",
  ResolveFailure: "Reunderwriting.ResolveFailure",
  ViewEscalations: "Reunderwriting.ViewEscalations",
  ResolveEscalation: "Reunderwriting.ResolveEscalation",
  ViewMetrics: "Reunderwriting.ViewMetrics",
  ViewAllMetrics: "Reunderwriting.ViewAllMetrics",
} as const;

// ---- PR 4: Failures (page-level) ----

export interface ReunderwritingFailureFilter {
  status?: FailureStatus;
  lane?: CriteriaLane;
  analyst?: string;
  caseId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

export interface PagedReunderwritingFailures {
  items: ReunderwritingFailureDto[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface UpdateReunderwritingFailureRequest {
  status?: FailureStatus;
  resolutionNotes?: string;
}

// ---- PR 4: Escalations ----

export type ReunderwritingEscalationStatus = "Pending" | "Approved" | "Denied";

export interface ReunderwritingEscalationRecordDto {
  id: string;
  reunderwritingCaseId: string;
  checklistItemId: string;
  lane: CriteriaLane;
  escalatedByUserId: string;
  escalatedAt: string;
  escalationReason: string;
  status: ReunderwritingEscalationStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  resolutionDirective: string | null;
}

export interface ReunderwritingEscalationListItem {
  id: string;
  reunderwritingCaseId: string;
  checklistItemId: string;
  lane: CriteriaLane;
  checklistItemCode: string | null;
  checklistItemTitle: string | null;
  caseNumber: string | null;
  escalatedByUserId: string;
  escalatedAt: string;
  escalationReason: string;
  status: ReunderwritingEscalationStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionDirective: string | null;
}

export interface ReunderwritingEscalationDetailDto {
  record: ReunderwritingEscalationRecordDto;
  checklistItemCode: string | null;
  checklistItemTitle: string | null;
  checklistItemDescription: string | null;
  caseNumber: string | null;
}

export interface ReunderwritingEscalationFilter {
  status?: ReunderwritingEscalationStatus;
  lane?: CriteriaLane;
  analyst?: string;
  caseId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

export interface PagedReunderwritingEscalations {
  items: ReunderwritingEscalationListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ResolveReunderwritingEscalationRequest {
  resolutionNotes: string;
  resolutionDirective: string;
}

// ---- PR 4: Metrics ----

export interface ReunderwritingMetricsFilter {
  from?: string;
  to?: string;
  stage?: ReunderwritingStage;
  eventTypeCode?: string;
  analyst?: string;
}

export interface ReunderwritingStageBucket {
  stage: ReunderwritingStage;
  label: string;
  caseCount: number;
}
export interface ReunderwritingPipelineMetricsResponse {
  stages: ReunderwritingStageBucket[];
  totalActive: number;
  totalCompleted: number;
  totalDeclined: number;
  totalWithdrawn: number;
}

export interface ReunderwritingDqrBucket {
  dqrValue: number;
  label: string;
  caseCount: number;
}
export interface ReunderwritingLaneFailureBucket {
  lane: CriteriaLane;
  label: string;
  failureCount: number;
}
export interface ReunderwritingTierFailureBucket {
  tier: string;
  label: string;
  failureCount: number;
}
export interface ReunderwritingFailureTypeBucket {
  catalogCode: string;
  catalogTitle: string;
  failureCount: number;
}
export interface ReunderwritingQualityMetricsResponse {
  dqr: ReunderwritingDqrBucket[];
  failuresByLane: ReunderwritingLaneFailureBucket[];
  failuresByTier: ReunderwritingTierFailureBucket[];
  topFailureTypes: ReunderwritingFailureTypeBucket[];
}

export interface ReunderwritingFailureStatusBucket {
  status: FailureStatus;
  label: string;
  count: number;
}
export interface ReunderwritingFailuresMetricsResponse {
  openCount: number;
  reviewedCount: number;
  resolvedCount: number;
  byStatus: ReunderwritingFailureStatusBucket[];
  byLane: ReunderwritingLaneFailureBucket[];
}

export interface ReunderwritingTimeToCompletionBucket {
  bucket: string;
  caseCount: number;
  avgDays: number;
}
export interface ReunderwritingTimeToCompletionResponse {
  avgDaysOverall: number;
  medianDaysOverall: number;
  completedCaseCount: number;
  byEventType: ReunderwritingTimeToCompletionBucket[];
}
